import { PayloadAction } from '@reduxjs/toolkit';
import { EventBusReceiveMessage } from 'features/application/types';
import { leaveSession } from 'features/room/actions';
import { applyMediaDevicesSaga } from 'features/sdk/applyMediaDevicesSaga';
import { onChangeBrandingOptionsCommandSaga } from 'features/sdk/onChangeBrandingOptionsCommandSaga';
import { onChangeToolbarPositionCommandSaga } from 'features/sdk/onChangeToolbarPositionCommandSaga';
import { onConfigureVBCommandSaga } from 'features/sdk/onConfigureVBCommandSaga';
import { onControlEventListenersCommandSaga } from 'features/sdk/onControlEventListenersCommandSaga';
import { onControlLocalTileCommandSaga } from 'features/sdk/onControlLocalTileCommandSaga';
import { onControlRemoteTileCommandSaga } from 'features/sdk/onControlRemoteTileCommandSaga';
import { onControlTileActionSaga } from 'features/sdk/onControlTileActionSaga';
import { onControlUICallbackSaga } from 'features/sdk/onControlUICallbackSaga';
import { onDisableVBCommandSaga } from 'features/sdk/onDisableVBCommandSaga';
import { onManageBroadcastCommandSaga } from 'features/sdk/onManageBroadcastCommandSaga';
import { onConfigureCaptionsCommandSaga } from 'features/sdk/onConfigureCaptionsCommandSaga';
import { onControlBroadcastCommandSaga } from 'features/sdk/onControlBroadcastCommandSaga';
import { onControlRemoteAudioCommandSaga } from 'features/sdk/onControlRemoteAudioCommandSaga';
import { onControlScreenshareCommandSaga } from 'features/sdk/onControlScreenshareCommandSaga';
import { onEndSessionCommandSaga } from 'features/sdk/onEndSessionCommandSaga';
import { onLowerHandCommandSaga } from 'features/sdk/onLowerHandCommandSaga';
import { onManageScreenshareCommandSaga } from 'features/sdk/onManageScreenshareCommandSaga';
import { onMuteFrameCommandSaga } from 'features/sdk/onMuteFrameCommandSaga';
import { onRaiseHandCommandSaga } from 'features/sdk/onRaiseHandCommandSaga';
import { onRemoveUserCommandSaga } from 'features/sdk/onRemoveUserCommandSaga';
import { reportCommandFailureSaga } from 'features/sdk/reportCommandFailureSaga';
import { onToggleCaptionsCommandSaga } from 'features/sdk/onToggleCaptionsCommandSaga';
import { onToggleRecordingCommandSaga } from 'features/sdk/onToggleRecordingCommandSaga';
import { onAddRecordingBookmarkCommandSaga } from 'features/sdk/onAddRecordingBookmarkCommandSaga';
import { toolbarVisibilityChanged, toolbarVisibilityToggled } from 'features/toolbar/toolbarSlice';
import { call, put, select } from 'redux-saga/effects';
import { selectRoomStatus } from 'features/room/roomSlice';
import { RoomStatus } from 'features/room/types';
import { eventBus, ReceiveMessageType } from 'utils/eventBus';
import { onControlTopbarCommandSaga } from 'features/sdk/onControlTopbarCommandSaga';
import { layoutModeChanged } from 'features/layout/features/config/configSlice';
import { onChangeRoleCommandSaga } from 'features/sdk/onChangeRoleCommandSaga';
import { onToggleWhiteboardSaga } from 'features/sdk/onToggleWhiteboardSaga';
import { onAddImageToWhiteboardSaga } from 'features/sdk/onAddImageToWhiteboardSaga';
import { onSetTemplateParamsSaga } from 'features/sdk/onSetTemplateParamsSaga';
import { onCreateWhiteboardSaga } from 'features/sdk/onCreateWhiteboardSaga';
import { onToggleLibraryFileSaga } from 'features/sdk/onToggleLibraryFileSaga';
import { onProcessCustomTileSaga } from 'features/sdk/onProcessCustomTileSaga';
import { onSendMessageToCustomTileSaga } from 'features/sdk/onSendMessageToCustomTileSaga';
import { onStartMobileScreenshareCommandSaga } from 'features/sdk/onStartMobileScreenshareCommandSaga';
import { onStopMobileScreenshareCommandSaga } from 'features/sdk/onStopMobileScreenshareCommandSaga';
import { onToggleRestreamingCommandSaga } from 'features/sdk/onToggleRestreamingCommandSaga';

const REQUIRE_JOIN_COMMANDS: ReadonlySet<ReceiveMessageType> = new Set<ReceiveMessageType>([
  'startScreenshare',
  'stopScreenshare',
  'startMobileScreenshare',
  'stopMobileScreenshare',
  'startRecording',
  'stopRecording',
  'addRecordingBookmark',
  'startRestreaming',
  'stopRestreaming',
  'requestToggleAudio',
  'requestMute',
  'requestUnmute',
  'removeUser',
  'raiseHand',
  'lowerHand',
  'allowBroadcast',
  'disallowBroadcast',
  'allowScreenshare',
  'disallowScreenshare',
  'changeRole',
  'endSession',
  'createWhiteboard',
  'openWhiteboard',
  'closeWhiteboard',
  'toggleWhiteboard',
  'addImageToWhiteboard',
  'openLibraryFile',
  'closeLibraryFile',
  'toggleLibraryFile',
  'sendMessageToCustomTile',
  'pinUser',
  'maximizeUser',
  'minimizeContent',
]);

export function* eventBusCommandSaga(action: PayloadAction<EventBusReceiveMessage>) {
  const { type, data } = action.payload;

  try {
    if (REQUIRE_JOIN_COMMANDS.has(type)) {
      const roomStatus: RoomStatus = yield select(selectRoomStatus);

      if (roomStatus !== 'joined') {
        yield call(
          eventBus.notAllowedError,
          `Command "${type}" requires an active session`,
          'not-joined',
          { command: type }
        );
        return;
      }
    }

    switch (type) {
      case 'setTemplateParams':
        yield call(onSetTemplateParamsSaga, data);
        break;
      case 'enableVideo':
      case 'enableAudio':
      case 'disableVideo':
      case 'disableAudio':
      case 'toggleVideo':
      case 'toggleAudio':
        yield call(onControlBroadcastCommandSaga, type);
        break;
      case 'createWhiteboard':
        yield call(onCreateWhiteboardSaga, data);
        break;
      case 'openWhiteboard':
        yield call(onToggleWhiteboardSaga, true, data);
        break;
      case 'closeWhiteboard':
        yield call(onToggleWhiteboardSaga, false, data);
        break;
      case 'toggleWhiteboard':
        yield call(onToggleWhiteboardSaga, data.show, data);
        break;
      case 'openLibraryFile':
        yield call(onToggleLibraryFileSaga, true, data);
        break;
      case 'closeLibraryFile':
        yield call(onToggleLibraryFileSaga, false, data);
        break;
      case 'toggleLibraryFile':
        yield call(onToggleLibraryFileSaga, data.show, data);
        break;
      case 'addImageToWhiteboard':
        yield call(onAddImageToWhiteboardSaga, data);
        break;
      case 'startScreenshare':
      case 'stopScreenshare':
        yield call(onControlScreenshareCommandSaga, type);
        break;

      case 'startMobileScreenshare':
        yield call(onStartMobileScreenshareCommandSaga, data);
        break;

      case 'stopMobileScreenshare':
        yield call(onStopMobileScreenshareCommandSaga, data);
        break;

      case 'startRecording':
        yield call(onToggleRecordingCommandSaga, true);
        break;

      case 'stopRecording':
        yield call(onToggleRecordingCommandSaga, false);
        break;

      case 'addRecordingBookmark':
        yield call(onAddRecordingBookmarkCommandSaga, data);
        break;

      case 'startRestreaming':
        yield call(onToggleRestreamingCommandSaga, true);
        break;

      case 'stopRestreaming':
        yield call(onToggleRestreamingCommandSaga, false);
        break;

      case 'showToolbar':
        yield put(toolbarVisibilityChanged(true));
        break;

      case 'hideToolbar':
        yield put(toolbarVisibilityChanged(false));
        break;

      case 'toggleToolbar':
        yield put(toolbarVisibilityToggled());
        break;

      case 'showTopbar':
        yield call(onControlTopbarCommandSaga, true);
        break;

      case 'hideTopbar':
        yield call(onControlTopbarCommandSaga, false);
        break;

      case 'toggleTopbar':
        yield call(onControlTopbarCommandSaga);
        break;

      case 'changeLayoutMode':
        yield put(layoutModeChanged(data));
        break;

      case 'leaveSession':
        yield put(leaveSession());

        break;
      case 'endSession':
        yield call(onEndSessionCommandSaga, data);
        break;

      case 'requestToggleAudio':
        yield call(onControlRemoteAudioCommandSaga, undefined, data);
        break;

      case 'requestMute':
        yield call(onControlRemoteAudioCommandSaga, true, data);
        break;

      case 'requestUnmute':
        yield call(onControlRemoteAudioCommandSaga, false, data);
        break;

      case 'removeUser':
        yield call(onRemoveUserCommandSaga, data);
        break;

      case 'toggleCaptions':
        yield call(onToggleCaptionsCommandSaga, undefined);
        break;

      case 'showCaptions':
        yield call(onToggleCaptionsCommandSaga, true);
        break;

      case 'hideCaptions':
        yield call(onToggleCaptionsCommandSaga, false);
        break;

      case 'configureCaptions':
        yield call(onConfigureCaptionsCommandSaga, data);
        break;

      case 'raiseHand':
        yield call(onRaiseHandCommandSaga);
        break;

      case 'lowerHand':
        yield call(onLowerHandCommandSaga, data);
        break;

      case 'allowBroadcast':
        // Check if data contains audio/video configuration
        if (typeof data === 'object' && (data.audio !== undefined || data.video !== undefined)) {
          yield call(onManageBroadcastCommandSaga, data.id, {
            audio: !!data.audio,
            video: !!data.video,
          });
        } else {
          // Backward compatibility
          yield call(onManageBroadcastCommandSaga, data, true);
        }
        break;

      case 'disallowBroadcast':
        yield call(onManageBroadcastCommandSaga, data, false);
        break;

      case 'allowScreenshare':
        yield call(onManageScreenshareCommandSaga, data, true);
        break;

      case 'disallowScreenshare':
        yield call(onManageScreenshareCommandSaga, data, false);
        break;

      case 'configureVirtualBackground':
        yield call(onConfigureVBCommandSaga, data, true);
        break;

      case 'disableVirtualBackground':
        yield call(onDisableVBCommandSaga);
        break;

      case 'muteFrame':
      case 'unmuteFrame':
      case 'toggleMuteFrame':
        yield call(onMuteFrameCommandSaga, type);
        break;

      case 'changeToolbarPosition':
        yield call(onChangeToolbarPositionCommandSaga, data);
        break;

      case 'changeBrandingOptions':
        yield call(onChangeBrandingOptionsCommandSaga, data);
        break;

      case 'minimizeLocalTile':
      case 'maximizeLocalTile':
        yield call(onControlLocalTileCommandSaga, type);
        break;

      case 'pinUser':
      case 'maximizeUser':
      case 'minimizeContent':
        yield call(onControlRemoteTileCommandSaga, type, data);
        break;

      case 'connectEventListener':
      case 'disconnectEventListener':
        yield call(onControlEventListenersCommandSaga, type, data);
        break;

      case 'connectUICallback':
      case 'disconnectUICallback':
        yield call(onControlUICallbackSaga, type, data);
        break;

      case 'changeRole':
        yield call(onChangeRoleCommandSaga, data);
        break;

      case 'addTileAction':
      case 'removeTileAction':
        yield call(onControlTileActionSaga, type, data);
        break;

      case 'addCustomTile':
      case 'removeCustomTile':
        yield call(onProcessCustomTileSaga, type, data);
        break;

      case 'sendMessageToCustomTile':
        yield call(onSendMessageToCustomTileSaga, data);
        break;

      /**
       * @deprecated This event has been removed since sdk v0.0.51
       */
      case 'applyMediaDevices':
        yield call(applyMediaDevicesSaga, data);
        break;

      default:
        break;
    }
  } catch (error) {
    yield call(reportCommandFailureSaga, error, type);
  }
}
